import { StrictMode, Suspense, lazy, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles/base.css";
import {
  applyThemeToDocument,
  hydrateTuning,
  themeStore,
  useMotionTuning,
} from "./lib/tuning.js";
import { TvApp } from "./tv/TvApp.js";
import { PhoneApp } from "./phone/PhoneApp.js";
import { DriftMarks } from "./DriftMarks.js";
import { resetSettings, updateSettings, useSettings } from "./lib/settings.js";
import { Tuner } from "./tuner/Tuner.js";
import { THEMES } from "../shared/themes.js";

// Theme lands before first paint so nothing flashes an unstyled frame.
applyThemeToDocument(themeStore.get());
themeStore.subscribe(() => applyThemeToDocument(themeStore.get()));
void hydrateTuning();

type Route = "tv" | "phone" | "dev" | "test" | "choose" | "settings";

function routeFor(pathname: string, search: string): Route {
  if (pathname.startsWith("/tv")) return "tv";
  // Both tools are dev-only. Their chunks do not exist in a production build,
  // so these fall through to the phone rather than rendering a shell with no
  // data behind it — the API routes they need 404 in production anyway.
  if (pathname.startsWith("/dev") && import.meta.env.DEV) return "dev";
  if (pathname.startsWith("/test") && import.meta.env.DEV) return "test";
  if (pathname.startsWith("/settings")) return "settings";
  if (pathname.startsWith("/phone")) return "phone";
  /*
   * A scanned QR or a tapped join link carries the room in the query, and that
   * is an instruction, not a question — send it straight to the phone. Only a
   * bare visit to the root is ambiguous, and only that gets asked which end of
   * the game it wants to be.
   */
  if (new URLSearchParams(search).has("room")) return "phone";
  return "choose";
}

/**
 * One tile of the ledger's ghost print, sixteen ruled lines tall. Entries are
 * placed by hand: sparse, off the centre column where the real content sits,
 * and never two on neighbouring lines.
 */
const LEDGER: Array<{
  line: number;
  x: string;
  leader?: string;
  points?: number;
  mark?: string;
  tint?: "rose" | "slate";
}> = [
  { line: 1, x: "6%", leader: "13%", points: 100 },
  { line: 3, x: "74%", leader: "9%", points: 65, tint: "slate" },
  { line: 6, x: "16%", mark: "?" },
  { line: 8, x: "62%", leader: "15%", points: 80, tint: "rose" },
  { line: 11, x: "9%", leader: "8%", points: 42 },
  { line: 13, x: "80%", mark: "?" },
  { line: 15, x: "36%", leader: "11%", points: 27 },
];

function LedgerTile() {
  return (
    <div className="landing__ledgerTile">
      {LEDGER.map((entry, i) => (
        <span
          key={i}
          className="landing__entry"
          data-tint={entry.tint}
          style={{ left: entry.x, top: `calc(47px * ${entry.line})` }}
        >
          {entry.leader && (
            <span
              className="landing__entryLeader"
              style={{ width: `min(${entry.leader}, 12rem)` }}
            />
          )}
          {entry.points !== undefined && (
            <span className="numeral">{entry.points}</span>
          )}
          {entry.mark && <span className="display">{entry.mark}</span>}
        </span>
      ))}
    </div>
  );
}

/**
 * The fork in the road. One device in the room runs the board; the rest join
 * from their phones, and both live at the same address.
 */
function Landing({ onPick }: { onPick: (route: Route) => void }) {
  const { landing, ambient } = useMotionTuning();
  const go = (path: string, route: Route) => {
    window.history.pushState({}, "", path);
    onPick(route);
  };

  return (
    <div
      className="landing grain"
      style={{
        ["--landing-rise" as string]: `${landing.riseDistance_px}px`,
        ["--landing-rise-ms" as string]: `${landing.riseDuration_ms}ms`,
        ["--landing-stagger" as string]: `${landing.stagger_ms}ms`,
      }}
    >
      <DriftMarks />
      {/*
       * The ghost print. The page is ruled ledger stock already feeding
       * downward; these are the faint entries printed on it — dotted leaders,
       * point values, the odd question mark — sitting exactly on the lines
       * and travelling at the paper's own speed. Two copies of one tile make
       * the roll seamless; the roll's duration is the rule feed times the
       * tile's line count, so print and rules never slip against each other.
       */}
      <div
        className="landing__ledger"
        aria-hidden="true"
        style={{ ["--print-opacity" as string]: ambient.printOpacity }}
      >
        <div className="landing__ledgerRoll">
          <LedgerTile />
          <LedgerTile />
        </div>
      </div>
      <div className="landing__inner">
        <h1 className="landing__title display riso" data-text="Fact Feud">
          Fact Feud
        </h1>
        <div className="landing__choices">
          <button
            className="landing__choice"
            onClick={() => go("/tv", "tv")}
            data-kind="tv"
          >
            Run the game
          </button>
          <button
            className="landing__choice"
            onClick={() => go("/phone", "phone")}
            data-kind="phone"
          >
            Join a game
          </button>
        </div>
        <button
          className="landing__settings"
          onClick={() => go("/settings", "settings")}
        >
          Settings
        </button>
      </div>
      <Tuner />
    </div>
  );
}

const DevApp = import.meta.env.DEV
  ? lazy(() => import("./dev/DevApp.js"))
  : null;
const TestApp = import.meta.env.DEV
  ? lazy(() => import("./test/TestApp.js"))
  : null;

function App() {
  const ambient = useMotionTuning().ambient;
  const [route, setRoute] = useState<Route>(() =>
    routeFor(window.location.pathname, window.location.search),
  );
  // True while the TV is showing the lobby — the only phase with room for a
  // back control and the only one where leaving is a reasonable thing to do.
  const [tvIdle, setTvIdle] = useState(true);

  useEffect(() => {
    const onPop = () =>
      setRoute(routeFor(window.location.pathname, window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // The ruled backdrop is painted by `::before` on each screen's root, which no
  // component owns, so its clock goes on the document instead.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--drift-rule-ms",
      `${ambient.ruleDrift_ms}ms`,
    );
  }, [ambient.ruleDrift_ms]);

  // Back to the fork in the road. Pushes state so the browser's own back button
  // agrees with the one on screen.
  const toStart = () => {
    window.history.pushState({}, "", "/");
    setRoute("choose");
  };

  if (route === "choose") return <Landing onPick={setRoute} />;
  if (route === "settings") return <SettingsScreen onBack={toStart} />;
  if (route === "tv") {
    return (
      // No back control once a match is running: it would sit on top of the
      // header chips, and leaving mid-round is not something to make easy.
      // The browser's own back button still works.
      <>
        <TvApp onIdle={setTvIdle} />
        {tvIdle ? <BackToStart onBack={toStart} /> : null}
      </>
    );
  }
  if (route === "dev" && DevApp) {
    return (
      <Suspense fallback={null}>
        <DevApp />
      </Suspense>
    );
  }
  if (route === "test" && TestApp) {
    return (
      <Suspense fallback={null}>
        <TestApp />
      </Suspense>
    );
  }
  return <PhoneApp onExit={toStart} />;
}

/**
 * Host settings.
 *
 * Only what a person setting up a game actually decides — the tuner behind
 * `/dev` is where the other two hundred numbers live, and it does not exist in
 * a production build. Everything here is stored on this device and sent to the
 * server when this browser opens a room, so it is the screen running the game
 * whose settings count.
 */
function SettingsScreen({ onBack }: { onBack: () => void }) {
  const settings = useSettings();
  const excluded = new Set(settings.excludedThemes);

  const toggleTheme = (id: string) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    updateSettings({ excludedThemes: [...next] });
  };

  return (
    <div className="landing grain settingsScreen">
      <DriftMarks />
      <div className="settings">
        <header className="settings__head">
          <button className="screenTag screenTag--inline" onClick={onBack}>
            <span aria-hidden="true">←</span>
            Start
          </button>
          <h1 className="settings__title display">Settings</h1>
        </header>

        <section className="settings__group">
          <h2>Match</h2>
          <SettingRow
            label="Rounds per match"
            value={settings.rounds}
            min={1}
            max={10}
            onChange={(v) => updateSettings({ rounds: v })}
          />
          <SettingRow
            label="Seconds to answer"
            value={settings.answerSeconds}
            min={5}
            max={120}
            step={5}
            onChange={(v) => updateSettings({ answerSeconds: v })}
          />
          <SettingRow
            label="Strikes allowed"
            value={settings.strikeLimit}
            min={1}
            max={6}
            onChange={(v) => updateSettings({ strikeLimit: v })}
          />
          <SettingRow
            label="Hardest difficulty"
            value={settings.maxDifficulty}
            min={1}
            max={5}
            onChange={(v) => updateSettings({ maxDifficulty: v })}
          />
        </section>

        <section className="settings__group">
          <h2>Sound</h2>
          <label className="settings__toggle">
            <input
              type="checkbox"
              checked={settings.soundOn}
              onChange={(e) => updateSettings({ soundOn: e.target.checked })}
            />
            <span>Play sound on the game screen</span>
          </label>
        </section>

        <section className="settings__group">
          <h2>Always ban</h2>
          <p className="settings__note">
            Struck out for good — these never reach the ban board, so nobody
            spends their one ban on them.
          </p>
          <div className="settings__themes">
            {THEMES.map((theme) => (
              <button
                key={theme.id}
                className="settings__theme"
                data-banned={excluded.has(theme.id)}
                onClick={() => toggleTheme(theme.id)}
              >
                {theme.label}
              </button>
            ))}
          </div>
          {excluded.size > THEMES.length - 6 ? (
            <p className="settings__warn">
              Too few themes left — the board needs at least six, so these are
              being ignored until you put some back.
            </p>
          ) : null}
        </section>

        <button className="settings__reset" onClick={() => resetSettings()}>
          Reset everything
        </button>
      </div>
    </div>
  );
}

function SettingRow({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="settingRow">
      <span className="settingRow__label">{label}</span>
      <div className="settingRow__control">
        <button
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={value <= min}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <span className="settingRow__value numeral">{value}</span>
        <button
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={value >= max}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Back to the choice between running the game and joining one.
 *
 * Named for where it goes, not for where it is: a back control labelled with
 * the current screen reads as an invitation to go to that screen, which is the
 * one place it cannot take you. Deliberately quiet — nobody should be drawn to
 * the one control that leaves the game — and it lifts on hover.
 */
function BackToStart({ onBack }: { onBack: () => void }) {
  return (
    <button className="screenTag" onClick={onBack}>
      <span aria-hidden="true">←</span>
      Start
    </button>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
